import { useEffect, useRef, useState } from "react";
import { Volume2, Square, Loader2 } from "lucide-react";

/* Free translate + text-to-speech bar for content pages.
   - Translate: injects Google's free Website Translator widget with 40+ languages.
     No API key needed. Renders once per page, then a compact select drives it.
   - Listen To: uses the browser's built-in Web Speech API (SpeechSynthesis),
     zero cost, zero server, works offline once voices are cached. Speaks the
     currently rendered page text in whichever language is picked.
*/

// 40+ ISO codes — Google Translate widget supports them all natively.
const LANGS = [
  ["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"],
  ["it", "Italiano"], ["pt", "Português"], ["nl", "Nederlands"], ["ru", "Русский"],
  ["pl", "Polski"], ["tr", "Türkçe"], ["ar", "العربية"], ["fa", "فارسی"],
  ["he", "עברית"], ["hi", "हिन्दी"], ["bn", "বাংলা"], ["ta", "தமிழ்"],
  ["te", "తెలుగు"], ["ml", "മലയാളം"], ["mr", "मराठी"], ["ur", "اردو"],
  ["zh-CN", "中文（简体）"], ["zh-TW", "中文（繁體）"], ["ja", "日本語"], ["ko", "한국어"],
  ["vi", "Tiếng Việt"], ["th", "ไทย"], ["id", "Bahasa Indonesia"], ["ms", "Bahasa Melayu"],
  ["fil", "Filipino"], ["sw", "Kiswahili"], ["am", "አማርኛ"], ["yo", "Yorùbá"],
  ["ig", "Igbo"], ["ha", "Hausa"], ["zu", "isiZulu"], ["af", "Afrikaans"],
  ["ro", "Română"], ["cs", "Čeština"], ["sk", "Slovenčina"], ["sv", "Svenska"],
  ["no", "Norsk"], ["fi", "Suomi"], ["da", "Dansk"], ["el", "Ελληνικά"],
  ["uk", "Українська"], ["hu", "Magyar"],
];

let widgetInitialised = false;

function installGoogleWidget() {
  if (widgetInitialised) return;
  widgetInitialised = true;
  window.googleTranslateElementInit = function () {
    if (!window.google || !window.google.translate) return;
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE, autoDisplay: false },
      "google_translate_element_hidden"
    );
  };
  const s = document.createElement("script");
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

function pickTranslateLang(code) {
  // Google widget stores its choice in the `googtrans` cookie. Setting it and
  // reloading is the standard, officially-documented way to change language.
  const host = window.location.hostname;
  const cookie = `/en/${code}`;
  document.cookie = `googtrans=${cookie}; path=/`;
  document.cookie = `googtrans=${cookie}; domain=${host}; path=/`;
  document.cookie = `googtrans=${cookie}; domain=.${host}; path=/`;
  window.location.reload();
}

export default function TranslateAndListen({ contentRef, className = "" }) {
  const [lang, setLang] = useState("en");
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const utterRef = useRef(null);

  useEffect(() => {
    installGoogleWidget();
    setSupported(typeof window.speechSynthesis !== "undefined");
    // Read the current translation from the googtrans cookie if present.
    const m = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
    if (m) setLang(m[1]);
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    };
  }, []);

  const changeLang = (code) => {
    setLang(code);
    if (code === "en") {
      // Reset — clear cookie & reload.
      document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      window.location.reload();
      return;
    }
    pickTranslateLang(code);
  };

  const speak = () => {
    if (!supported) return;
    const el = contentRef?.current || document.querySelector("main") || document.body;
    // Grab visible innerText, strip long whitespace, cap at ~4k chars per utterance.
    const text = (el.innerText || "").replace(/\s+/g, " ").trim().slice(0, 4000);
    if (!text) return;
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang.startsWith("zh") ? lang : (lang.length === 2 ? lang : lang.split("-")[0]);
    u.rate = 0.96;
    u.pitch = 1.0;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    utterRef.current = u;
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const stop = () => {
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setSpeaking(false);
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="translate-listen-bar">
      <label className="inline-flex items-center gap-1.5 text-[11px] font-800 uppercase tracking-widest text-slate-500">
        Translate
        <select
          value={lang}
          onChange={(e) => changeLang(e.target.value)}
          data-testid="translate-select"
          className="border border-slate-300 rounded-full px-2.5 py-1 text-sm font-500 text-slate-800 bg-white focus:outline-none focus:border-blue-500 max-w-[160px] normal-case">
          {LANGS.map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
      </label>
      {supported && (
        speaking ? (
          <button onClick={stop} data-testid="listen-stop"
            className="inline-flex items-center gap-1 text-xs font-800 border border-rose-500 text-rose-700 px-2.5 py-1 rounded-full hover:bg-rose-50">
            <Square className="w-3.5 h-3.5" /> Stop
          </button>
        ) : (
          <button onClick={speak} data-testid="listen-start"
            className="inline-flex items-center gap-1 text-xs font-800 border border-blue-500 text-blue-700 px-2.5 py-1 rounded-full hover:bg-blue-50">
            <Volume2 className="w-3.5 h-3.5" /> Listen to page
          </button>
        )
      )}
      {/* Hidden mount for the Google Translate widget. `.skiptranslate` avoids Google re-translating this control bar. */}
      <div id="google_translate_element_hidden" className="hidden" translate="no" />
    </div>
  );
}
